import { useId } from 'react';

const VIEW_W = 320;
const VIEW_H = 180;
const PAD_L = 40;
const PAD_R = 14;
const PAD_T = 12;
const PAD_B = 24;

function scaleY(v, max, plotH) {
  if (!Number.isFinite(v) || max <= 0) return PAD_T + plotH;
  const t = Math.min(Math.max(v / max, 0), 1);
  return PAD_T + plotH * (1 - t);
}

function niceMax(v) {
  if (!Number.isFinite(v) || v <= 0) return 1;
  const pow = Math.pow(10, String(Math.floor(v)).length - 1);
  return Math.ceil(v / pow) * pow;
}

function MiniLineChart({ title, days, values, formatValue, predictionRaw, predictionUnit, predictionLoading = false }) {
  const gradIdRaw = useId();
  const gradId = `daily-grad-${gradIdRaw.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const n = values.length;
  const fmt = formatValue ?? ((v) => String(v));
  const predNum = predictionRaw === undefined || predictionRaw === null ? null : Number(predictionRaw);
  const hasPred = predNum !== null && Number.isFinite(predNum);
  const predShown = hasPred ? fmt(Math.round(predNum)) : '—';
  const unit = predictionUnit ? ` ${predictionUnit}` : '';

  if (n === 0) {
    return (
      <div className="daily-chart daily-chart--empty">
        <div className="daily-chart-title">{title}</div>
        <p className="daily-chart-emptyMsg">Нет данных</p>
      </div>
    );
  }

  const nums = values.map((v) => (Number.isFinite(Number(v)) ? Number(v) : 0));
  const seriesMax = Math.max(0, ...nums, hasPred ? predNum : 0);
  const max = niceMax(seriesMax * 1.15 || 1);
  const plotW = VIEW_W - PAD_L - PAD_R;
  const plotH = VIEW_H - PAD_T - PAD_B;

  const scaleX = (i) => {
    if (hasPred) return PAD_L + (plotW / n) * i;
    if (n <= 1) return PAD_L + plotW / 2;
    return PAD_L + (i / (n - 1)) * plotW;
  };

  const pts = nums.map((v, i) => `${scaleX(i)},${scaleY(v, max, plotH)}`);
  const linePath = n === 1 ? '' : `M ${pts.join(' L ')}`;
  const lastX = scaleX(n - 1);
  const lastY = scaleY(nums[n - 1], max, plotH);
  const areaD = linePath
    ? `${linePath} L ${lastX} ${PAD_T + plotH} L ${scaleX(0)} ${PAD_T + plotH} Z`
    : '';

  const tickVals = [0, 1, 2, 3].map((t) => (max / 3) * t);
  const labelIndexes = [0, Math.floor((n - 1) / 2), n - 1].filter(
    (ix, pos, arr) => arr.indexOf(ix) === pos
  );

  const dayLabel = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    const m = iso.slice(5, 7);
    const d = iso.slice(8, 10);
    return `${d}.${m}`;
  };

  const predX = hasPred ? scaleX(n) : null;
  const predY = hasPred ? scaleY(predNum, max, plotH) : null;

  return (
    <div className="daily-chart">
      <div className="daily-chart-head">
        <div className="daily-chart-title">{title}</div>
        <span className="daily-chart-legend">
          <i />
          факт
        </span>
      </div>
      <svg
        className="daily-chart-svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`${title}: ${n} дн.`}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(46, 107, 240, 0.22)" />
            <stop offset="100%" stopColor="rgba(46, 107, 240, 0)" />
          </linearGradient>
        </defs>

        {tickVals.map((tv, idx) => {
          const y = scaleY(tv, max, plotH);
          return (
            <g key={idx}>
              <line
                className="daily-chart-grid"
                x1={PAD_L}
                y1={y}
                x2={VIEW_W - PAD_R}
                y2={y}
              />
              <text className="daily-chart-yTick" x={PAD_L - 7} y={y + 3} textAnchor="end">
                {fmt(tv)}
              </text>
            </g>
          );
        })}

        {areaD ? <path d={areaD} fill={`url(#${gradId})`} stroke="none" /> : null}

        {linePath ? (
          <path
            className="daily-chart-line"
            d={linePath}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <circle className="daily-chart-point" cx={lastX} cy={lastY} r={3} />
        )}

        {nums.map((v, i) => (
          <circle
            key={i}
            className={v > 0 ? 'daily-chart-point' : 'daily-chart-point daily-chart-point--zero'}
            cx={scaleX(i)}
            cy={scaleY(v, max, plotH)}
            r={v > 0 ? 3 : 2}
          />
        ))}

        {hasPred ? (
          <>
            <line
              className="daily-chart-predLine"
              x1={lastX}
              y1={lastY}
              x2={predX}
              y2={predY}
            />
            <circle className="daily-chart-predDot" cx={predX} cy={predY} r={4.5} />
          </>
        ) : null}

        {labelIndexes.map((i) => (
          <text
            key={`${days[i]}-${i}`}
            className="daily-chart-xTick"
            x={scaleX(i)}
            y={VIEW_H - 8}
            textAnchor="middle"
          >
            {dayLabel(days[i])}
          </text>
        ))}
      </svg>
      <div className="daily-chart-prediction">
        Прогноз на завтра:{' '}
        {predictionLoading ? (
          <span className="daily-stats-skel daily-stats-skel--predictionValue" aria-hidden="true" />
        ) : (
          <b>{predShown}{unit}</b>
        )}
        <span className="daily-chart-legend daily-chart-legend--pred">
          <i />
          прогноз
        </span>
      </div>
    </div>
  );
}

export default MiniLineChart;
